import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@material-ui/core';
import cx from 'classnames';
import { forwardRef, ForwardRefRenderFunction, useMemo } from 'react';

import { withStyles, WithStyles } from '@core/theme/utils/with-styles';
import { AppId, useConfigAll } from '@shared/contexts/ConfigContext';

import { styles } from './Tooltip.styles';

export enum TooltipDelay {
  Default = 0,
  Delayed = 400,
  Browser = 1000,
}

export enum TooltipSize {
  Default = 'default',
  Small = 'small',
}

export type TooltipRef = HTMLDivElement;

export interface TooltipProps extends Omit<MuiTooltipProps, 'classes' | 'size'>, WithStyles<typeof styles> {
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  delay?: TooltipDelay;
  size?: TooltipSize;
}

const TooltipComponent: ForwardRefRenderFunction<TooltipRef, TooltipProps> = (
  {
    arrow = true,
    children,
    classes,
    containerProps,
    onClick,
    placement,
    size: suppliedSize,
    title,
    delay,
    ...otherProps
  },
  ref
) => {
  const { appId } = useConfigAll();

  const size: TooltipSize = useMemo(() => {
    if (suppliedSize) {
      return suppliedSize;
    }

    switch (appId) {
      case AppId.Atlas:
        return TooltipSize.Small;
      default:
        return TooltipSize.Default;
    }
  }, [appId, suppliedSize]);

  return (
    <MuiTooltip
      {...otherProps}
      arrow={arrow}
      classes={{
        arrow: cx(classes.arrow, classes[size]),
        tooltip: cx(classes.tooltip, classes[size]),
        tooltipPlacementBottom: classes.tooltipPlacementVertical,
        tooltipPlacementTop: classes.tooltipPlacementVertical,
        tooltipPlacementLeft: classes.tooltipPlacementHorizontal,
        tooltipPlacementRight: classes.tooltipPlacementHorizontal,
      }}
      className={cx(classes.root)}
      enterDelay={delay ?? TooltipDelay.Default}
      enterNextDelay={delay ?? TooltipDelay.Default}
      placement={placement}
      ref={ref}
      title={title}
    >
      <div {...containerProps} className={cx(containerProps?.className, classes.container)}>
        {children}
      </div>
    </MuiTooltip>
  );
};

export const Tooltip = withStyles(styles)(forwardRef(TooltipComponent));
